import {
    rem,
    type ColorScheme,
    type MantineThemeOverride,
    type Tuple,
} from '@mantine/core';
import type {} from 'csstype';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './styles/mantine-overrides/tooltip.module.css';

type ColorTuple = Tuple<string, 10>;
const createColorTuple = (input: string | ColorTuple): ColorTuple => {
    if (typeof input === 'string') {
        return new Array(10).fill(input) as ColorTuple;
    }
    return input;
};

const lightdashDarkGray = createColorTuple([
    '#141414',
    '#1f1f1f',
    '#242424',
    '#2e2e2e',
    '#3b3b3b',
    '#525252',
    '#7a7a7a',
    '#9e9e9e',
    '#c8c8c8',
    '#d9d9d9',
]);

const lightModeColors = {
    background: createColorTuple('#FEFEFE'),
    foreground: createColorTuple('#1A1B1E'),

    ldDark: createColorTuple([
        '#C9C9C9',
        '#b8b8b8',
        '#828282',
        '#696969',
        '#424242',
        '#3b3b3b',
        '#2e2e2e',
        '#242424',
        '#1f1f1f',
        '#141414',
    ]),

    ldGray: createColorTuple([
        '#f8f9fa',
        '#f1f3f5',
        '#e9ecef',
        '#dee2e6',
        '#ced4da',
        '#adb5bd',
        '#868e96',
        '#495057',
        '#343a40',
        '#212529',
    ]),
};

const darkModeColors = {
    background: createColorTuple('#1a1a1a'),
    foreground: createColorTuple('#FEFEFE'),

    /** Overwrite Mantine's dark colors because they are too light */
    dark: createColorTuple([
        '#A1A1A1',
        '#939393',
        '#686868',
        '#545454',
        '#353535',
        '#292929',
        '#202020',
        '#191919',
        '#151515',
        '#0E0E0E',
    ]),

    /** Make both light and dark the same shades to avoid clashing different tones. */
    ldDark: lightdashDarkGray,
    ldGray: lightdashDarkGray,
};

// Colors used for conditional formatting in dark mode
export const DARK_MODE_COLORS = {
    SUBTLE_GRAY: darkModeColors.ldDark[4],
    CONTRAST_GRAY: darkModeColors.ldDark[6],
} as const;

export interface LightdashFieldColors {
    /** CSS variable for background color (auto-switches for dark/light modes) */
    bg: string;
    /** CSS variable for hover background color */
    bgHover: string;
    /** CSS variable for text color */
    color: string;
    /** CSS variable for column header text color */
    columnHeaderColor: string;
    /** Mantine color token for component color property */
    mantineColor: string;
}

export const LD_FIELD_COLORS = {
    dimension: {
        bg: 'light-dark(#EDF0FD, #202539)',
        bgHover: 'light-dark(#4b69ef28, #4b69ef35)',
        color: 'light-dark(#3b5bdb, #95aaf0)',
        columnHeaderColor: 'light-dark(#1c2b67, #93acff)',
        mantineColor: 'dimension',
    },
    metric: {
        bg: 'light-dark(#FBE9E0, #3E2F1A)',
        bgHover: 'light-dark(#e8590c30, #81510d75)',
        color: 'light-dark(#de7f0b, #e08a20)',
        columnHeaderColor: 'light-dark(#502e06, #de7f0b)',
        mantineColor: 'metric',
    },
    calculation: {
        bg: 'light-dark(#EBF5ED, #1D3525)',
        bgHover: 'light-dark(#2f9e4428, #23753565)',
        color: 'light-dark(#2b8a3e, #38af4d)',
        columnHeaderColor: 'light-dark(#1b5326, #48b95d)',
        mantineColor: 'calculation',
    },
    DEFAULT: {
        bg: 'var(--mantine-color-gray-light)',
        bgHover: 'var(--mantine-color-gray-light-hover)',
        color: 'var(--mantine-color-gray-light-color)',
        columnHeaderColor: 'var(--mantine-color-gray-light-color)',
        mantineColor: 'ldGray',
    },
} satisfies {
    dimension: LightdashFieldColors;
    metric: LightdashFieldColors;
    calculation: LightdashFieldColors;
    DEFAULT: LightdashFieldColors;
};

export const getMantineThemeOverride = (
    colorScheme: ColorScheme,
    overrides?: {
        components?: Partial<MantineThemeOverride['components']>;
    },
) =>
    ({
        colorScheme,
        ...overrides,

        focusRing: 'auto',

        //Black value from Blueprint. We could change this.
        // Without it things look a little darker than before.
        black: '#111418',

        colors: colorScheme === 'dark' ? darkModeColors : lightModeColors,

        spacing: {
            one: rem(1),
            two: rem(2),
            xxs: rem(4),
            xs: rem(8),
            sm: rem(12),
            md: rem(16),
            lg: rem(20),
            xl: rem(24),
            xxl: rem(32),
            '3xl': rem(40),
            '4xl': rem(48),
            '5xl': rem(64),
            '6xl': rem(80),
            '7xl': rem(96),
            '8xl': rem(128),
            '9xl': rem(160),
        },

        fontFamily: [
            'Inter',
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Roboto',
            'Oxygen',
            'Ubuntu',
            'Cantarell',
            'Fira Sans',
            'Droid Sans',
            'Open Sans',
            'Helvetica Neue',
            'Apple Color Emoji',
            'Segoe UI Emoji',
            'sans-serif',
        ].join(', '),

        lineHeight: 1.4,

        cursorType: 'pointer',

        shadows: {
            subtle: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
            heavy: '0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 2px 2px -1px rgba(10, 13, 18, 0.04)',
            bottomFade: '0 0 0 1px #bec1c426',
        },

        components: {
            Button: {
                defaultProps: {
                    variant: 'darkPrimary',
                    radius: 'md',
                },
                variants: {
                    darkPrimary: (theme) => ({
                        root: {
                            background: `var(--mantine-color-foreground-0)`,
                            borderRadius: theme.radius.md,
                            color: `var(--mantine-color-ldGray-0)`,
                            ...theme.fn.hover({
                                background: `color-mix(in srgb, var(--mantine-color-foreground-0) 80%, transparent)`,
                            }),
                            '&[data-loading]': {
                                boxShadow: theme.shadows.subtle,
                            },
                            '&[data-disabled]': {
                                boxShadow: theme.shadows.subtle,
                                color: `color-mix(in srgb, var(--mantine-color-foreground-0) 50%, transparent)`,
                            },
                        },
                    }),
                },
            },
            Kbd: {
                styles: (theme, _params) => ({
                    root: {
                        borderBottomWidth: theme.spacing.two,
                    },
                }),
            },

            Tooltip: {
                classNames: {
                    tooltip: styles.tooltipMantine6,
                },
                defaultProps: {
                    withArrow: true,
                },
                variants: {
                    xs: (theme) => ({
                        tooltip: {
                            fontSize: theme.fontSizes.xs,
                        },
                    }),
                },
            },

            Modal: {
                defaultProps: {
                    // FIXME: This makes the mantine modals line up exactly with the Blueprint ones.
                    // It could be made a less-magic number once we migrate
                    yOffset: 140,
                },
            },

            Alert: {
                styles: (_theme, _params) => ({
                    title: {
                        // FIXME: This makes the icon align with the title.
                        lineHeight: 1.55,
                    },
                }),
            },

            ...overrides?.components,
        },

        other: {
            transitionTimingFunction: 'ease-in-out',
            transitionDuration: 200, // in ms
            chartFont: 'Inter, sans-serif',
            ldField: LD_FIELD_COLORS,
            explorerItemBg: {
                dimension: {
                    light: '#d2dbe9',
                    dark: '#2a3f5f',
                },
                metric: {
                    light: '#e4dad0',
                    dark: '#4a3929',
                },
                calculation: {
                    light: '#d2dfd7',
                    dark: '#2a4a2f',
                },
            },
        },

        globalStyles: (theme) => ({
            ':root': {
                '--table-selected-bg':
                    theme.colorScheme === 'dark'
                        ? theme.colors.blue[9]
                        : '#ECF6FE',
                '--table-selected-border':
                    theme.colorScheme === 'dark'
                        ? theme.colors.blue[5]
                        : '#4170CB',
            },

            'html, body': {
                backgroundColor:
                    theme.colorScheme === 'dark'
                        ? theme.colors.ldDark[0]
                        : theme.colors.ldGray[0],
            },

            body: {
                fontSize: '14px',
            },

            p: {
                marginBottom: '10px',
                marginTop: 0,
            },

            b: {
                fontWeight: 'bold',
            },

            strong: {
                fontWeight: 600,
            },

            '.ace_editor.ace_autocomplete': {
                width: '500px',
            },
            '.ace_editor *': {
                fontFamily:
                    "Menlo, 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace",
            },
            '.wmde-markdown, .wmde-markdown-var': {
                fontFamily: theme.fontFamily,
            },
            '.wmde-markdown img': {
                backgroundColor: 'transparent',
            },
            // CJK font selection for headless browser screenshots/PDFs.
            // Chromium on Linux ignores fontconfig, so we use :lang() CSS
            // selectors to explicitly pick the correct Noto Sans CJK variant.
            ':lang(ja)': {
                fontFamily: `'Noto Sans CJK JP', ${theme.fontFamily}`,
            },
            ':lang(zh-CN), :lang(zh-Hans)': {
                fontFamily: `'Noto Sans CJK SC', ${theme.fontFamily}`,
            },
            ':lang(zh-TW), :lang(zh-Hant)': {
                fontFamily: `'Noto Sans CJK TC', ${theme.fontFamily}`,
            },
            ':lang(ko)': {
                fontFamily: `'Noto Sans CJK KR', ${theme.fontFamily}`,
            },

            '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 },
            },
            ...(theme.colorScheme === 'dark'
                ? {
                      '[class*="mantine-"][data-with-border]': {
                          border: `1px solid var(--mantine-color-ldDark-2)`,
                      },
                  }
                : undefined),
        }),
    }) satisfies MantineThemeOverride;
